import { memo } from 'react';
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import { cn } from '@/lib/utils';
import { visibleLabel } from '@/lib/mindmap-markdown';
import type { LaidOutNode } from './sideTree';
import { isWrapped, pillKind } from './measureLabel';
import { MindmapLabel } from './MindmapLabel';

/**
 * The four learning-type tokens, cycled per concept (roadmap step), as the
 * marketing mindmap cycles them per branch. Classes rather than hex: nodes are DOM, so the
 * tokens (and their dark-mode variants) resolve normally.
 */
export const BRANCH_TONES = [
  { border: 'border-lesson/40', badge: 'bg-lesson/15', stroke: 'text-lesson' },
  { border: 'border-notes/40', badge: 'bg-notes/15', stroke: 'text-notes' },
  {
    border: 'border-practice/40',
    badge: 'bg-practice/15',
    stroke: 'text-practice',
  },
  {
    border: 'border-reference/40',
    badge: 'bg-reference/15',
    stroke: 'text-reference',
  },
] as const;

export function branchTone(branch: number) {
  return BRANCH_TONES[((branch % 4) + 4) % 4];
}

export type MindmapFlowNode = Node<
  LaidOutNode & {
    dimmed: boolean;
    animate: boolean;
    /** A preview's hinted node: drawn faint and wordless. */
    ghost?: boolean;
    /** A preview's entrance delay (ms), replacing the map's own stagger. */
    delay?: number;
    /** Present on a node naming a lesson concept: opens its panel. */
    onOpen?: () => void;
  },
  'mindmap'
>;

/**
 * Invisible anchor points: left/right for parent-to-child edges, top/bottom
 * for the roadmap path between steps. The map is not editable.
 */
const HANDLES = [
  { id: 's-left', type: 'source', position: Position.Left },
  { id: 's-right', type: 'source', position: Position.Right },
  { id: 's-bottom', type: 'source', position: Position.Bottom },
  { id: 't-left', type: 'target', position: Position.Left },
  { id: 't-right', type: 'target', position: Position.Right },
  { id: 't-top', type: 'target', position: Position.Top },
] as const;

/** One line: a pill. Wrapped onto several lines (`measurePill` sized the box
 *  for them): a rounded card, its lines centred. */
const PILL_SHAPE = {
  line: { box: 'whitespace-nowrap rounded-full', label: undefined },
  wrapped: { box: 'rounded-2xl text-center', label: 'min-w-0 break-words' },
} as const;

function pillShape({ depth, h }: { depth: number; h: number }) {
  return PILL_SHAPE[isWrapped(depth, h) ? 'wrapped' : 'line'];
}

const HANDLE =
  '!pointer-events-none !h-px !w-px !min-h-0 !min-w-0 !border-0 !bg-transparent';

/**
 * The roadmap draws step by step: step k's spine segment, then the concept,
 * then its sub-nodes column by column. The main node goes first.
 */
export function enterDelay({
  branch,
  depth,
}: {
  branch: number;
  depth: number;
}) {
  if (branch < 0) return '0ms';
  return `${branch * 90 + 60 + Math.max(0, depth - 1) * 50}ms`;
}

/** A node naming a lesson concept is a button: Tab reaches it, Enter or
 *  Space opens the concept (a click is handled by the canvas). */
function conceptButtonProps(data: MindmapFlowNode['data']) {
  const open = data.onOpen;
  if (!open) return {};
  return {
    role: 'button',
    tabIndex: 0,
    'aria-label': `Open concept: ${visibleLabel(data.fullLabel)}`,
    'data-concept-node': true,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      open();
    },
  };
}

function MindmapNodeView({ data }: NodeProps<MindmapFlowNode>) {
  const kind = pillKind(data.depth);
  const tone = branchTone(data.branch);
  const shape = pillShape(data);
  return (
    <div
      data-mindmap-node
      {...conceptButtonProps(data)}
      title={
        data.fullLabel !== data.label ? visibleLabel(data.fullLabel) : undefined
      }
      style={{
        width: data.w,
        height: data.h,
        animationDelay: data.animate ? entranceDelay(data) : undefined,
      }}
      className={cn(
        'flex cursor-pointer items-center justify-center border bg-card transition-opacity duration-150',
        shape.box,
        data.animate && 'mindmap-enter',
        data.dimmed && 'opacity-30',
        kind === 'root' &&
          'border-brand-500/35 px-4 text-sm font-semibold text-foreground shadow-[0_6px_20px_-8px_hsl(var(--brand-600)/0.45)]',
        kind === 'branch' &&
          cn('px-3 text-xs font-medium text-foreground', tone.border),
        kind === 'leaf' &&
          'border-foreground/10 px-2.5 font-mono text-xs text-muted-foreground',
        // A concept opens its panel: a darker label and an accent edge set it
        // apart from key points at the same depth (same font, same size).
        data.onOpen &&
          'border-primary/35 text-foreground outline-none hover:border-primary/70 focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      {kind === 'branch' && (
        // The concept's step on the roadmap path, 1-based.
        <span
          aria-hidden
          className={cn(
            'mr-1.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full font-mono text-xs text-foreground',
            tone.badge
          )}
        >
          {data.branch + 1}
        </span>
      )}
      <MindmapLabel label={data.label} className={shape.label} />
      {HANDLES.map(({ id, type, position }) => (
        <Handle
          key={id}
          id={id}
          type={type}
          position={position}
          isConnectable={false}
          className={HANDLE}
        />
      ))}
    </div>
  );
}

/** A preview's hinted node: a faint, wordless pill at the end of a line that
 *  fades out toward it. */
function entranceDelay(data: MindmapFlowNode['data']): string {
  return data.delay !== undefined ? `${data.delay}ms` : enterDelay(data);
}

function GhostNode({ data }: { data: MindmapFlowNode['data'] }) {
  // A faint pill the size the real one would be, with blank bars where its
  // words go: there is clearly a next idea, but not which. The entrance runs
  // on this wrapper so it ends at the inner pill's own faintness.
  return (
    <div
      aria-hidden
      style={{
        width: data.w,
        height: data.h,
        animationDelay: data.animate ? entranceDelay(data) : undefined,
      }}
      className={cn(data.animate && 'mindmap-enter')}
    >
      <div
        className={cn(
          'flex h-full w-full items-center gap-1.5 rounded-full border bg-card/70 px-3 opacity-45',
          branchTone(data.branch).border
        )}
      >
        <span className='h-1.5 w-[55%] rounded-full bg-muted-foreground/20' />
        <span className='h-1.5 w-[25%] rounded-full bg-muted-foreground/15' />
      </div>
      {HANDLES.map(({ id, type, position }) => (
        <Handle
          key={id}
          id={id}
          type={type}
          position={position}
          isConnectable={false}
          className={HANDLE}
        />
      ))}
    </div>
  );
}

function MindmapNodeOrGhost(props: NodeProps<MindmapFlowNode>) {
  const { data } = props;
  return data.ghost ? (
    <GhostNode data={data} />
  ) : (
    <MindmapNodeView {...props} />
  );
}

export const MindmapNodeComponent = memo(MindmapNodeOrGhost);
